// ---------------------------------------------------------------------------
// Canonical key form (lowercase) — preferred for new code.
// ---------------------------------------------------------------------------
export type TierKey = 'silver' | 'gold' | 'platinum';

/**
 * Canonical tier order (lowest → highest). Frozen; spread
 * (`[...TIER_KEYS]`) when a mutable copy is needed.
 */
export const TIER_KEYS: readonly TierKey[] = ['silver', 'gold', 'platinum'] as const;

/** Display labels (Title-Case). Mirrors the names stored in SQL. */
export const TIER_LABELS = {
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
} as const satisfies Record<TierKey, string>;

/**
 * Maps a tier name (in any casing, with surrounding whitespace) back
 * to its {@link TierKey}, or returns null if it doesn't match a known
 * tier. Inverse of {@link TIER_LABELS}.
 */
export const tierKeyFromName = (name: string): TierKey | null => {
  const normalized = name.trim().toLowerCase();
  return (TIER_KEYS as readonly string[]).includes(normalized)
    ? (normalized as TierKey)
    : null;
};

/**
 * Title-Case tier name. Derived from {@link TIER_LABELS} so renaming
 * a label automatically narrows this type — no parallel literal to
 * keep in sync.
 */
export type Tier = (typeof TIER_LABELS)[TierKey];

/** Title-Case tier list, derived from {@link TIER_KEYS}. */
export const TIER_OPTIONS: Tier[] = TIER_KEYS.map((k) => TIER_LABELS[k]);

/**
 * Tiers retired from the active program that still appear in historical
 * records — e.g. Change History rows promoted from Bronze. Display-only:
 * deliberately absent from {@link TIER_KEYS}, {@link TIER_LABELS} and
 * {@link TIER_OPTIONS} so they can never be picked for a new change.
 */
export type RetiredTier = 'Bronze';

export const TIER_COLORS: Record<Tier | RetiredTier, string> = {
  Bronze: '#A74109',
  Silver: '#5C5C5C',
  Gold: '#B27C00',
  Platinum: '#5C2E91',
};

export const getTierColor = (tier: string): string =>
  TIER_COLORS[tier as Tier | RetiredTier] ?? TIER_COLORS.Silver;
